import api from './api';
import { BUILTIN_INTERVALS, intervalLabel } from '../utils/intervalCatalog';

/** The scheduler cadences a scan can be driven on. */
export interface ScanCadence {
  scheduleType: 'MINUTE' | 'HOUR' | 'DAY';
  scheduleValue: number;
}

export interface CustomIntervalRow {
  id: number;
  minutes: number;
  label: string;
  enabledForScan: boolean;
  sortOrder: number;
  /** Whether a scheduled scan may point at this interval. True for anything
   *  chartable — the cadence and the bar size are separate questions. */
  scanEligible: boolean;
  /** Whether a scheduler cadence EQUALS this bar size (i.e. it divides 1440).
   *  False for 7H and every other non-divisor: those are scanned by ticking on a
   *  finer grid and emitting once per CLOSED bar, not by matching the bar. */
  cadenceCoincident?: boolean;
  /** The cadence to tick on so no close of this interval is missed. Computed to
   *  be safe for a watchlist that mixes Delta and NSE — i.e. the FINEST of the
   *  per-venue answers below. Use it only when the venues are unknown. */
  scanCadence?: ScanCadence;
  /** The same, per venue. A Delta-only 7H schedule wants DELTA's answer: NSE's
   *  is finer only because a 7H bucket there is longer than the whole session,
   *  which is not an interval anyone would use on NSE. */
  scanCadenceByVenue?: Record<string, ScanCadence>;
  /** That cadence explained in the user's words. */
  cadenceNote?: string;
}

export interface IntervalVocabulary {
  builtin: number[];
  custom: CustomIntervalRow[];
  /** What a scheduled scan may point at: qualifying builtins + opted-in customs. */
  scanEligible: number[];
}

export interface IntervalReferences {
  scheduledScans: Array<{ id: number; strategyId: number; enabled: boolean; autoTrade: boolean }>;
  htfMappings: Array<{ id: number; interval: number; htfInterval: number }>;
  blockedByLiveAutoTrade: boolean;
}

/** Used until the API answers, and whenever it cannot be reached. */
export const FALLBACK_VOCABULARY: IntervalVocabulary = {
  builtin: [...BUILTIN_INTERVALS],
  custom: [],
  scanEligible: [...BUILTIN_INTERVALS],
};

export const customIntervalService = {
  async list(): Promise<IntervalVocabulary> {
    const res = await api.get<any>('/intervals');
    const d = res.data?.data;
    if (!d) return FALLBACK_VOCABULARY;
    return { builtin: d.builtin || [], custom: d.custom || [], scanEligible: d.scanEligible || [] };
  },

  async upsert(input: { minutes: number; label?: string; enabledForScan?: boolean; sortOrder?: number }): Promise<CustomIntervalRow> {
    const res = await api.post<any>('/intervals', input);
    return res.data?.data;
  },

  async references(minutes: number): Promise<IntervalReferences> {
    const res = await api.get<any>(`/intervals/${minutes}/references`);
    return res.data?.data;
  },

  /** Removes it from the picker. Anything still pointing at these minutes keeps
   *  working — see the controller for why that is deliberate. Rejects with a 409
   *  when an enabled auto-trading scan is using it. */
  async remove(minutes: number): Promise<void> {
    await api.delete(`/intervals/${minutes}`);
  },
};

/** The full picker list: builtins in ladder order, then the user's own. */
export function pickerIntervals(vocab: IntervalVocabulary): Array<{ value: number; label: string; custom: boolean }> {
  const builtin = (vocab.builtin.length ? vocab.builtin : [...BUILTIN_INTERVALS])
    .map(value => ({ value, label: intervalLabel(value), custom: false }));
  const custom = [...vocab.custom]
    .sort((a, b) => (a.sortOrder - b.sortOrder) || (a.minutes - b.minutes))
    .filter(c => !builtin.some(b => b.value === c.minutes))
    .map(c => ({ value: c.minutes, label: c.label || intervalLabel(c.minutes), custom: true }));
  return [...builtin, ...custom];
}
